import { flushSync } from 'react-dom';
import { useEffect, useRef } from 'react';
import { getInteractionModality } from '../interactions';
import { focusWithoutScrolling, mergeProps, scrollIntoView, scrollIntoViewport, useEvent, useRouter } from '../utils';
import { useLocale } from '../i18n';
import { focusSafely, getFocusableTreeWalker } from '../focus';
import { isCtrlKeyPressed, isNonContiguousSelectionModifier } from './utils';
import { useTypeSelect } from './useTypeSelect';
/**
 * Handles interactions with selectable collections.
 */ export function useSelectableCollection(options) {
    const { selectionManager: manager, keyboardDelegate: delegate, ref, autoFocus = false, shouldFocusWrap = false, disallowEmptySelection = false, disallowSelectAll = false, selectOnFocus = manager.selectionBehavior === 'replace', disallowTypeAhead = false, shouldUseVirtualFocus, allowsTabNavigation = false, isVirtualized, // If no scrollRef is provided, assume the collection ref is the scrollable region
    scrollRef = ref, linkBehavior = 'action' } = options;
    const { direction } = useLocale();
    const router = useRouter();
    const onKeyDown = (e)=>{
        var _ref_current;
        // Prevent option + tab from doing anything since it doesn't move focus to the cells, only buttons/checkboxes
        if (e.altKey && e.key === 'Tab') {
            e.preventDefault();
        }
        // Keyboard events bubble through portals. Don't handle keyboard events
        // for elements outside the collection (e.g. menus).
        if (!((_ref_current = ref.current) === null || _ref_current === void 0 ? void 0 : _ref_current.contains(e.target))) {
            return;
        }
        const navigateToKey = (key, childFocus)=>{
            if (key != null) {
                if (manager.isLink(key) && linkBehavior === 'selection' && selectOnFocus && !isNonContiguousSelectionModifier(e)) {
                    var _scrollRef_current;
                    // Set focused key and re-render synchronously to bring item into view if needed.
                    flushSync(()=>{
                        manager.setFocusedKey(key, childFocus);
                    });
                    const item = key != null && key !== '' ? (_scrollRef_current = scrollRef.current) === null || _scrollRef_current === void 0 ? void 0 : _scrollRef_current.querySelector(`[data-key="${key}"]`) : null;
                    if (item) {
                        router.open(item, e);
                    }
                    return;
                }
                // Ensure the selection manager is marked as focused when navigating via keyboard.
                // shouldUseVirtualFocus = only set focused for virtual focus scenarios to avoid
                // side effects in components that use real DOM focus (e.g. Menu or ListBox).
                // https://jira.corp.stripe.com/browse/RUN_SAILUI-510
                if (!manager.isFocused && shouldUseVirtualFocus) {
                    manager.setFocused(true);
                }
                manager.setFocusedKey(key, childFocus);
                if (manager.isLink(key) && linkBehavior === 'override') {
                    return;
                }
                if (e.shiftKey && manager.selectionMode === 'multiple') {
                    manager.extendSelection(key);
                } else if (selectOnFocus && !isNonContiguousSelectionModifier(e)) {
                    manager.replaceSelection(key);
                }
            }
        };
        switch(e.key){
            case 'ArrowDown':
                {
                    if (delegate.getKeyBelow) {
                        var _delegate_getFirstKey;
                        e.preventDefault();
                        let nextKey = manager.focusedKey != null ? delegate.getKeyBelow(manager.focusedKey) : (_delegate_getFirstKey = delegate.getFirstKey) === null || _delegate_getFirstKey === void 0 ? void 0 : _delegate_getFirstKey.call(delegate);
                        if (nextKey == null && shouldFocusWrap) {
                            var _delegate_getFirstKey1;
                            nextKey = (_delegate_getFirstKey1 = delegate.getFirstKey) === null || _delegate_getFirstKey1 === void 0 ? void 0 : _delegate_getFirstKey1.call(delegate, manager.focusedKey);
                        }
                        navigateToKey(nextKey);
                    }
                    break;
                }
            case 'ArrowUp':
                {
                    if (delegate.getKeyAbove) {
                        var _delegate_getLastKey;
                        e.preventDefault();
                        let nextKey = manager.focusedKey != null ? delegate.getKeyAbove(manager.focusedKey) : (_delegate_getLastKey = delegate.getLastKey) === null || _delegate_getLastKey === void 0 ? void 0 : _delegate_getLastKey.call(delegate);
                        if (nextKey == null && shouldFocusWrap) {
                            var _delegate_getLastKey1;
                            nextKey = (_delegate_getLastKey1 = delegate.getLastKey) === null || _delegate_getLastKey1 === void 0 ? void 0 : _delegate_getLastKey1.call(delegate, manager.focusedKey);
                        }
                        navigateToKey(nextKey);
                    }
                    break;
                }
            case 'ArrowLeft':
                {
                    if (delegate.getKeyLeftOf) {
                        e.preventDefault();
                        let nextKey = delegate.getKeyLeftOf(manager.focusedKey);
                        if (nextKey == null && shouldFocusWrap) {
                            var _delegate_getFirstKey2, _delegate_getLastKey2;
                            nextKey = direction === 'rtl' ? (_delegate_getFirstKey2 = delegate.getFirstKey) === null || _delegate_getFirstKey2 === void 0 ? void 0 : _delegate_getFirstKey2.call(delegate, manager.focusedKey) : (_delegate_getLastKey2 = delegate.getLastKey) === null || _delegate_getLastKey2 === void 0 ? void 0 : _delegate_getLastKey2.call(delegate, manager.focusedKey);
                        }
                        navigateToKey(nextKey, direction === 'rtl' ? 'first' : 'last');
                    }
                    break;
                }
            case 'ArrowRight':
                {
                    if (delegate.getKeyRightOf) {
                        e.preventDefault();
                        let nextKey = delegate.getKeyRightOf(manager.focusedKey);
                        if (nextKey == null && shouldFocusWrap) {
                            var _delegate_getLastKey3, _delegate_getFirstKey3;
                            nextKey = direction === 'rtl' ? (_delegate_getLastKey3 = delegate.getLastKey) === null || _delegate_getLastKey3 === void 0 ? void 0 : _delegate_getLastKey3.call(delegate, manager.focusedKey) : (_delegate_getFirstKey3 = delegate.getFirstKey) === null || _delegate_getFirstKey3 === void 0 ? void 0 : _delegate_getFirstKey3.call(delegate, manager.focusedKey);
                        }
                        navigateToKey(nextKey, direction === 'rtl' ? 'last' : 'first');
                    }
                    break;
                }
            case 'Home':
                if (delegate.getFirstKey) {
                    e.preventDefault();
                    const firstKey = delegate.getFirstKey(manager.focusedKey, isCtrlKeyPressed(e));
                    manager.setFocusedKey(firstKey);
                    if (isCtrlKeyPressed(e) && e.shiftKey && manager.selectionMode === 'multiple') {
                        manager.extendSelection(firstKey);
                    } else if (selectOnFocus) {
                        manager.replaceSelection(firstKey);
                    }
                }
                break;
            case 'End':
                if (delegate.getLastKey) {
                    e.preventDefault();
                    const lastKey = delegate.getLastKey(manager.focusedKey, isCtrlKeyPressed(e));
                    manager.setFocusedKey(lastKey);
                    if (isCtrlKeyPressed(e) && e.shiftKey && manager.selectionMode === 'multiple') {
                        manager.extendSelection(lastKey);
                    } else if (selectOnFocus) {
                        manager.replaceSelection(lastKey);
                    }
                }
                break;
            case 'PageDown':
                if (delegate.getKeyPageBelow) {
                    e.preventDefault();
                    const nextKey = delegate.getKeyPageBelow(manager.focusedKey);
                    navigateToKey(nextKey);
                }
                break;
            case 'PageUp':
                if (delegate.getKeyPageAbove) {
                    e.preventDefault();
                    const nextKey = delegate.getKeyPageAbove(manager.focusedKey);
                    navigateToKey(nextKey);
                }
                break;
            case 'a':
                if (isCtrlKeyPressed(e) && manager.selectionMode === 'multiple' && disallowSelectAll !== true) {
                    e.preventDefault();
                    manager.selectAll();
                }
                break;
            case 'Escape':
                e.preventDefault();
                if (!disallowEmptySelection) {
                    manager.clearSelection();
                }
                break;
            case 'Tab':
                {
                    if (!allowsTabNavigation) {
                        // There may be elements that are "tabbable" inside a collection (e.g. in a grid cell).
                        // However, collections should be treated as a single tab stop, with arrow key navigation internally.
                        // We don't control the rendering of these, so we can't override the tabIndex to prevent tabbing.
                        // Instead, we handle the Tab key, and move focus manually to the first/last tabbable element
                        // in the collection, so that the browser default behavior will apply starting from that element
                        // rather than the currently focused one.
                        if (e.shiftKey) {
                            ref.current.focus();
                        } else {
                            const walker = getFocusableTreeWalker(ref.current, {
                                tabbable: true
                            });
                            let next;
                            let last;
                            do {
                                last = walker.lastChild();
                                if (last) {
                                    next = last;
                                }
                            }while (last)
                            if (next && !next.contains(document.activeElement)) {
                                focusWithoutScrolling(next);
                            }
                        }
                        break;
                    }
                    break;
                }
            default:
                break;
        }
    };
    // Store the scroll position so we can restore it later.
    const scrollPos = useRef({
        top: 0,
        left: 0
    });
    useEvent(scrollRef, 'scroll', // @ts-expect-error - Type 'null' is not assignable to type '(this: Document, ev: Event) => any'.
    isVirtualized ? null : ()=>{
        scrollPos.current = {
            top: scrollRef.current.scrollTop,
            left: scrollRef.current.scrollLeft
        };
    });
    const onFocus = (e)=>{
        if (manager.isFocused) {
            // If a focus event bubbled through a portal, reset focus state.
            if (!e.currentTarget.contains(e.target)) {
                manager.setFocused(false);
            }
            return;
        }
        // Focus events can bubble through portals. Ignore these events.
        if (!e.currentTarget.contains(e.target)) {
            return;
        }
        manager.setFocused(true);
        if (manager.focusedKey == null) {
            const navigateToFirstKey = (key)=>{
                if (key != null) {
                    manager.setFocusedKey(key);
                    if (selectOnFocus) {
                        manager.replaceSelection(key);
                    }
                }
            };
            // If the user hasn't yet interacted with the collection, there will be no focusedKey set.
            // Attempt to detect whether the user is tabbing forward or backward into the collection
            // and either focus the first or last item accordingly.
            const relatedTarget = e.relatedTarget;
            if (relatedTarget && // eslint-disable-next-line no-bitwise
            e.currentTarget.compareDocumentPosition(relatedTarget) & Node.DOCUMENT_POSITION_FOLLOWING) {
                var _delegate_getLastKey;
                var _manager_lastSelectedKey;
                navigateToFirstKey((_manager_lastSelectedKey = manager.lastSelectedKey) !== null && _manager_lastSelectedKey !== void 0 ? _manager_lastSelectedKey : (_delegate_getLastKey = delegate.getLastKey) === null || _delegate_getLastKey === void 0 ? void 0 : _delegate_getLastKey.call(delegate));
            } else {
                var _delegate_getFirstKey;
                var _manager_firstSelectedKey;
                navigateToFirstKey((_manager_firstSelectedKey = manager.firstSelectedKey) !== null && _manager_firstSelectedKey !== void 0 ? _manager_firstSelectedKey : (_delegate_getFirstKey = delegate.getFirstKey) === null || _delegate_getFirstKey === void 0 ? void 0 : _delegate_getFirstKey.call(delegate));
            }
        } else if (!isVirtualized) {
            // Restore the scroll position to what it was before.
            scrollRef.current.scrollTop = scrollPos.current.top;
            scrollRef.current.scrollLeft = scrollPos.current.left;
        }
        if (!isVirtualized && manager.focusedKey != null && manager.focusedKey !== '') {
            var _scrollRef_current;
            // Refocus and scroll the focused item into view if it exists within the scrollable region.
            const element = (_scrollRef_current = scrollRef.current) === null || _scrollRef_current === void 0 ? void 0 : _scrollRef_current.querySelector(`[data-key="${manager.focusedKey}"]`);
            if (element) {
                // This prevents a flash of focus on the first/last element in the collection, or the collection itself.
                if (!element.contains(document.activeElement)) {
                    focusWithoutScrolling(element);
                }
                const modality = getInteractionModality();
                if (modality === 'keyboard') {
                    scrollIntoViewport(element, {
                        containingElement: ref.current
                    });
                }
            }
        }
    };
    const onBlur = (e)=>{
        // Don't set blurred and then focused again if moving focus within the collection.
        if (!e.currentTarget.contains(e.relatedTarget)) {
            manager.setFocused(false);
        }
    };
    const autoFocusRef = useRef(autoFocus);
    useEffect(()=>{
        if (autoFocusRef.current) {
            let focusedKey = null;
            // Check focus strategy to determine which item to focus
            if (autoFocus === 'first') {
                var _delegate_getFirstKey;
                focusedKey = (_delegate_getFirstKey = delegate.getFirstKey) === null || _delegate_getFirstKey === void 0 ? void 0 : _delegate_getFirstKey.call(delegate);
            }
            if (autoFocus === 'last') {
                var _delegate_getLastKey;
                focusedKey = (_delegate_getLastKey = delegate.getLastKey) === null || _delegate_getLastKey === void 0 ? void 0 : _delegate_getLastKey.call(delegate);
            }
            // If there are any selected keys, make the first one the new focus target
            const selectedKeys = manager.selectedKeys;
            if (selectedKeys.size) {
                focusedKey = selectedKeys.values().next().value;
            }
            manager.setFocused(true);
            manager.setFocusedKey(focusedKey !== null && focusedKey !== void 0 ? focusedKey : null);
            // If no default focus key is selected, focus the collection itself.
            if (focusedKey == null && !shouldUseVirtualFocus) {
                focusSafely(ref.current);
            }
        }
        autoFocusRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // If not virtualized, scroll the focused element into view when the focusedKey changes.
    // When virtualized, Virtualizer handles this internally.
    const lastFocusedKey = useRef(manager.focusedKey);
    useEffect(()=>{
        const modality = getInteractionModality();
        if (manager.isFocused && manager.focusedKey != null && manager.focusedKey !== '' && (scrollRef === null || scrollRef === void 0 ? void 0 : scrollRef.current)) {
            const element = scrollRef.current.querySelector(`[data-key="${manager.focusedKey}"]`);
            if (element && modality === 'keyboard') {
                if (!isVirtualized) {
                    scrollIntoView(scrollRef.current, element);
                }
                scrollIntoViewport(element, {
                    containingElement: ref.current
                });
            }
        }
        // If the focused key becomes null (e.g. the last item is deleted), focus the whole collection.
        if (manager.isFocused && manager.focusedKey == null && lastFocusedKey.current != null) {
            focusSafely(ref.current);
        }
        lastFocusedKey.current = manager.focusedKey;
    }, [
        isVirtualized,
        scrollRef,
        manager.focusedKey,
        manager.isFocused,
        ref
    ]);
    let handlers = {
        onKeyDown,
        onFocus,
        onBlur,
        onMouseDown (e) {
            // Ignore events that bubbled through portals.
            if (scrollRef.current === e.target) {
                // Prevent focus going to the collection when clicking on the scrollbar.
                e.preventDefault();
            }
        }
    };
    const { typeSelectProps } = useTypeSelect({
        keyboardDelegate: delegate,
        selectionManager: manager
    });
    if (!disallowTypeAhead) {
        handlers = mergeProps(typeSelectProps, handlers);
    }
    // If nothing is focused within the collection, make the collection itself tabbable.
    // This will be marshalled to either the first or last item depending on where focus came from.
    // If using virtual focus, don't set a tabIndex at all so that VoiceOver on iOS 14 doesn't try
    // to move real DOM focus to the element anyway.
    let tabIndex;
    if (!shouldUseVirtualFocus) {
        tabIndex = manager.focusedKey == null ? 0 : -1;
    }
    return {
        collectionProps: {
            ...handlers,
            tabIndex
        }
    };
}

//# sourceMappingURL=useSelectableCollection.js.map